import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { UserPlus, Users } from "lucide-react";
import { api } from "@/lib/api";
import { toApiError } from "@/lib/api-error";
import { useAuthStore } from "@/lib/auth";
import { useTenantMe } from "@/lib/tenant-me";
import { formatMoney } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/v2/PageHeader";
import { DataTable, type Column } from "@/components/v2/DataTable";
import { AddClientDialog } from "@/components/v2/AddClientDialog";
import { EmptyState } from "@/components/v2/EmptyState";
import { ClientDetailPanel } from "@/components/v2/ClientDetailPanel";

/**
 * Clients — Pencil frame `aCKT0`. Customer directory with avatar + name + email
 * + phone + lifetime value + first-seen.
 */

type Customer = {
  id?: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  lifetimeValue?: number | string;
  firstSeenAt?: string;
};

const initialsOf = (c: Customer): string => {
  const a = (c.firstName ?? "").charAt(0).toUpperCase();
  const b = (c.lastName ?? "").charAt(0).toUpperCase();
  if (a || b) return (a + b) || a || b;
  return (c.email ?? "?").charAt(0).toUpperCase();
};

export function Clients() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const tenant = useTenantMe();
  const currency = tenant.data?.tenant.currency ?? "PHP";
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(30);
  const [params, setParams] = useSearchParams();

  // `?focus=<id>` deep-link — keeps parity with the existing
  // `/clients?focus=<id>` link from booking-panel/Details.tsx.
  const focusId = params.get("focus");

  const openCustomer = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set("focus", id);
    else next.delete("focus");
    setParams(next, { replace: true });
  };

  const list = useQuery({
    queryKey: ["customers", q, page, pageSize],
    enabled: !!accessToken,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/customers", {
        params: { query: { q: q || undefined, page, size: pageSize } },
      });
      if (error) throw toApiError(error);
      return data;
    },
  });

  const columns: Column<Customer>[] = [
    {
      key: "name",
      header: "Client",
      cell: (c) => {
        const name = [c.firstName, c.lastName].filter(Boolean).join(" ") || c.email || "(unnamed)";
        return (
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-primary-light text-primary flex items-center justify-center text-xs font-semibold">
              {initialsOf(c)}
            </div>
            <div className="min-w-0">
              <p className="font-medium text-sm truncate">{name}</p>
              {c.email && (
                <p className="text-xs text-muted-foreground truncate">{c.email}</p>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: "phone",
      header: "Phone",
      cell: (c) => <span className="text-sm text-muted-foreground">{c.phone ?? "—"}</span>,
    },
    {
      key: "ltv",
      header: "Lifetime value",
      align: "right",
      sortAccessor: (c) => Number(c.lifetimeValue ?? 0),
      cell: (c) => (
        <span className="font-medium text-sm">
          {formatMoney(currency, c.lifetimeValue)}
        </span>
      ),
    },
    {
      key: "first",
      header: "First seen",
      sortAccessor: (c) => (c.firstSeenAt ? new Date(c.firstSeenAt) : null),
      cell: (c) => (
        <span className="text-xs text-muted-foreground">
          {c.firstSeenAt ? new Date(c.firstSeenAt).toLocaleDateString() : "—"}
        </span>
      ),
    },
  ];

  return (
    <div className="px-6 py-6 space-y-6">
      <PageHeader
        title="Clients"
        subtitle="Customer directory + lifetime value."
        actions={
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <UserPlus className="h-3.5 w-3.5 mr-1.5" />
            Add client
          </Button>
        }
      />

      {list.isError && <p className="text-sm text-destructive">{(list.error as Error).message}</p>}

      {list.data && (
        <DataTable
          columns={columns}
          rows={(list.data.content ?? []) as Customer[]}
          rowKey={(c) => c.id ?? Math.random().toString()}
          onRowClick={(c) => c.id && openCustomer(c.id)}
          loading={list.isLoading}
          sortable
          resizable
          hideable
          searchable={{
            value: q,
            onChange: (v) => {
              setQ(v);
              setPage(0);
            },
            placeholder: "Search name, email, or phone…",
          }}
          pagination={{
            pageIndex: page,
            pageSize,
            pageCount: list.data.totalPages ?? 1,
            totalRows: list.data.totalElements,
            onPageChange: setPage,
            onPageSizeChange: (n) => {
              setPageSize(n);
              setPage(0);
            },
          }}
          exportCsv={{
            filename: `clients-${new Date().toISOString().slice(0, 10)}.csv`,
          }}
          emptyState={
            <div className="p-8">
              <EmptyState
                icon={Users}
                title={q ? "No matches" : "No clients yet"}
                description={
                  q
                    ? `No customer matches "${q}".`
                    : "Clients are created automatically the first time someone books a service."
                }
              />
            </div>
          }
        />
      )}

      <ClientDetailPanel
        customerId={focusId}
        onClose={() => openCustomer(null)}
      />

      <AddClientDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(id) => {
          qc.invalidateQueries({ queryKey: ["customers"] });
          openCustomer(id);
        }}
      />
    </div>
  );
}
